/**
 * Utility functions for flight date change flow
 */

/**
 * Creates a combined fare key for upsell lookup
 * The key format is 'journeyId1,journeyId2,...' matching all legs in order
 */
export const createCombinedFareKey = (
  selectedJourneys: Record<string, App.JourneyV2>,
  allLegs: Array<App.LegWithFlightItemId>,
): string => {
  return allLegs
    .map(leg => selectedJourneys[leg.id]?.id)
    .filter(Boolean)
    .join(',')
}

/**
 * Checks if upsell data exists for a journey combination
 */
export const hasUpsellDataForCombination = (
  fareKey: string,
  fareIdsToUpsellFarePerLeg: Record<string, App.FlightDateChangeUpsellFaresResult>,
): boolean => {
  return !!fareIdsToUpsellFarePerLeg[fareKey]?.upsellPerLeg?.length
}

/**
 * Gets the complete journey selection including user selections and auto-selected unchanged legs
 * For unchanged legs, respects pairedFareId relationships using bookingInfo.boookingKey
 */
export const getCompleteJourneySelection = (
  userSelectedJourneys: Record<string, App.JourneyV2>,
  allAvailableJourneys: Array<Array<App.JourneyV2>>,
  selectedLegs: Array<App.LegWithFlightItemIdWithNewDepartureDate>,
  allLegs: Array<App.LegWithFlightItemId>,
): Record<string, App.JourneyV2> => {
  const result: Record<string, App.JourneyV2> = { ...userSelectedJourneys }

  // Build a map of journeys by bookingKey for each leg (used for pairing)
  const journeysByBookingKeyPerLeg = allAvailableJourneys.map(journeys => {
    const map = new Map<string, App.JourneyV2>()
    journeys.forEach(journey => {
      if (journey.bookingInfo?.boookingKey) {
        map.set(journey.bookingInfo.boookingKey, journey)
      }
    })
    return map
  })

  // Auto-select journeys for unchanged legs, respecting pairedFareId
  allLegs.forEach((leg, legIndex) => {
    const isSelectedForChange = selectedLegs.some(selectedLeg => selectedLeg.id === leg.id)

    // If this leg is not being changed and not already selected
    if (!isSelectedForChange && !result[leg.id] && allAvailableJourneys[legIndex]?.length > 0) {
      let foundPairedJourney: App.JourneyV2 | undefined

      // Check all user-selected journeys for pairing information
      for (const selectedJourney of Object.values(userSelectedJourneys)) {
        if (selectedJourney.pairedFareId?.length) {
          // Find a journey in this leg whose bookingKey matches the paired list
          for (const pairedBookingKey of selectedJourney.pairedFareId) {
            const pairedJourney = journeysByBookingKeyPerLeg[legIndex].get(pairedBookingKey)
            if (pairedJourney) {
              foundPairedJourney = pairedJourney
              break
            }
          }
        }
        if (foundPairedJourney) break
      }

      // Use paired journey if found, otherwise fall back to first available
      result[leg.id] = foundPairedJourney || allAvailableJourneys[legIndex][0]
    }
  })

  return result
}

/**
 * Determines the current page location in the flight date change flow based on URL pathname.
 * Used for reliable navigation since Redux state may not always match the actual page.
 */
export function getFlightChangeDatesPageLocation(pathname: string) {
  return {
    isOnFlightChangePage: pathname.includes('flight-change-select-flight-to-change'),
    isOnInsurancePage: pathname.includes('insurance-select-dates'),
    isOnPurchasePage: pathname.includes('/purchase'),
  }
}
